<template>
    <Modal @close="onClose">
        <template #content>
            <Standard :uiTitle="uiTitle"
                :uiDisabled="false"
                :uiIsGlobal="false"
                class="add-edit-modal br-m"
                :class="[widgetSelected ? 'widget-selected' : '', isFullScreen ? 'el--fullscreen' : '']"
                :uiWidth="0"
                :uiBodyClass="isFullScreen ? 'sp-b-0 dh-10' : ''"
                :uiFooterClass="'sp-t-0'"
                :uiFooter="!isFullScreen"
                @close="onClose">
                <template #titleBefore>
                    <!-- Back arrow -->
                    <ButtonIcon v-if="step===2 && !hasOneChildType && !edit_mode"
                        class="sm-r-s"
                        icon="chevron-left"
                        qa="add-edit-back-button"
                        @click="step--" />
                </template>
                <template #titleAction>
                    <ButtonIcon v-if="!isConfigValid"
                        class="el--t-warning sm-r-xs"
                        icon="warning"
                        @click="onShowConfigValidation" />
                    <template v-if="isFullScreen">
                        <AthButton v-if="showContinueButton"
                            class="el--s-s el--t-secondary el--inline sm-r-xs"
                            :lbl="'mead.continue'"
                            :disabled="!widgetSelected"
                            qa="add-edit-continue-button"
                            @click="step++" />
                        <AthButton v-else
                            class="el--s-s el--t-secondary el--inline sm-r-xs"
                            :lbl="uiConfirm"
                            :disabled="!isConfigValid"
                            qa="add-edit-confirm-button"
                            @click="onConfirm" />
                    </template>
                </template>
                <template #body>
                    <!-- General Description -->
                    <p class="sm-b-l">{{ uiDesc | athT }}</p>
                    <div v-if="step===2 && hasConfiguration" class="divider-line b-b"></div>

                    <!-- Widget Selection -->
                    <p v-if="step===1" class="sm-b-xxs">{{ 'mead.supported_widgets' | athT }}</p>
                    <DropdownCategorized v-if="step===1"
                        class="c-p dw-10 el--s-m sm-b-l"
                        :usePopover="false"
                        :value="'mead.add_a_supported_widget'"
                        :options="childOptions"
                        :extraOptions="[{value: 'none', label: 'mead.add_a_supported_widget'}]"
                        @input="onWidgetSelected" />

                    <!-- Widget Customization -->
                    <div v-if="widgetSelected" class="ath-row dh-10">
                        <!-- Widget Customization -->
                        <template v-if="step===2 && hasConfiguration">
                            <div class="configuration-section col-xs-12 col-sm-12 col-md-4 col-lg-4 b-r sp-r-l">
                                <DashboardItemConfig :config="child_props"
                                    :sections="configurationSections"
                                    :dashboardType="dashboardType"
                                    :dashboard="dashboard"
                                    :widgetType="selected_child_type"
                                    class="sp-t-m"
                                    @onConfigUpdated="onConfigUpdated" />
                            </div>
                        </template>

                        <!-- Widget Preview -->
                        <div class="widget-preview-section col-xs-12 col-sm-12"
                            :class="[step===2 && hasConfiguration ? 'sp-l-l sp-t-m col-md-8 col-lg-8' : 'col-md-12 col-lg-12']">
                            <p v-if="step===2 && hasConfiguration"
                                class="te-fat-section sp-b-m">{{ 'mead.preview' | athT }}</p>
                            <div class="widget-blueprint-area sp-xxl br-m bf">
                                <comp v-bind="child_props"
                                    class="widget-item"
                                    :widgetType="selected_child_type"
                                    :dashboardType="dashboardType"
                                    :dashboard="dashboard"
                                    :preview="true"
                                    :is="getWidget()" />
                            </div>
                            <!-- Widget title and description -->
                            <div class="sp-r-m sp-b-l sp-l-m sp-t-l">
                                <div class="te-fat-section sp-b-xs">{{ uiWidgetTitle | athT }}</div>
                                <div class="te-subnote">{{ uiWidgetDesc | athT }}</div>
                            </div>
                        </div>
                    </div>
                    <!-- Bottom Divider line -->
                    <div v-if="step===2 && hasConfiguration && !isFullScreen" class="divider-line b-b"></div>
                </template>
                <template #footer>
                    <AthButton v-if="showContinueButton"
                        class="el--s-m el--t-secondary"
                        :lbl="'mead.continue'"
                        :disabled="!widgetSelected"
                        qa="add-edit-continue-button"
                        @click="step++" />
                    <AthButton v-else
                        class="el--s-m el--t-secondary"
                        :lbl="uiConfirm"
                        :disabled="!isConfigValid"
                        qa="add-edit-confirm-button"
                        @click="onConfirm" />
                </template>
            </Standard>
        </template>
    </Modal>
</template>
<script>
import { isNeObject } from '@aretheyhappy/utils';
import { DataStudioSubType } from '@aretheyhappy/lib-taxonomy/data_studio/data_studio_types';
import Modal from '../../standalone_modals/Modal.vue';
import Standard from '../../modal-styles/Standard.vue';
import AthButton from '../../../atoms/Button.vue';
import DashboardItemConfig from '../configuration/DashboardItemConfig.vue';
import ButtonIcon from '../../../atoms/ButtonIcon.vue';
import DropdownCategorized from '../../dropdown-categorized/DropdownCategorized.vue';
import DashboardConfigValidationPopover from '../popover/DashboardConfigValidationPopover.vue';

export default {
    name: 'mead-dashboard-add-edit-modal',
    props: {
        /**
         * Dashboard type sent in from parent.
         */
        dashboardType: {type: String, default: () => ''},
        /**
         * Row type sent in from parent. Used to determine which widget types
         * can be added to the row.
         */
        rowType: {type: String, default: () => ''},
        /**
         * Starting child props (edit mode). When an object is provided (rather
         * than false), the modal opens in edit mode and seeds its
         * configuration from these props.
         */
        childProps: {type: [Object, Boolean], default: () => {return false;}},
        /**
         * Sent child type (edit mode). Used to seed the selected widget type
         * when opening the modal in edit mode.
         */
        childType: {type: String, default: () => ''},
        /**
         * Weight that can be added still in the dashboard. Used to determine
         * which widgets are available to select and disable ones that would
         * exceed the remaining capacity.
         */
        availableWeight: {type: Number, default: () => 0},
        /**
         * Request start in edit mode, even when childProps alone would not
         * indicate editing.
         */
        requestEditMode: {type: Boolean, default: false},
        /**
         * Entire Dashboard object.
         */
        dashboard: {type: Object, default: () => ({})},
    },
    components: {DashboardItemConfig, Modal, DropdownCategorized, AthButton, Standard, ButtonIcon},
    data () {
        const edit_mode = isNeObject(this.childProps) || this.requestEditMode;

        return {
            edit_mode,
            step: edit_mode ? 2 : 1,
            selected_child_type: edit_mode ? this.childType : 'none',
            child_props: edit_mode ? this.childProps : {},
        };
    },
    computed: {
        /**
         * Modal Title
         */
        uiTitle: function () {
            if (this.step === 1) {
                return 'mead.add_widget';
            }
            else if (this.step === 2) {
                return 'mead.configure_widget'
            }
            return '';
        },
        /**
         * Modal Description
         */
        uiDesc: function () {
            if (this.step === 1) {
                return 'mead.choose_a_supported_widget';
            }
            else if (this.step === 2) {
                return 'mead.review_and_customize_the_settings'
            }
            return '';
        },
        /**
         * Confirm button text
         */
        uiConfirm: function () {
            if (this.edit_mode) {
                return 'mead.update_widget';
            }
            if ([DataStudioSubType.EXCEL, DataStudioSubType.PDF].includes(this.dashboardType)) {
                return 'mead.add_to_report';
            }

            return 'mead.add_to_dashboard';
        },
        /**
         * Descriptive title for selected widget
         */
        uiWidgetTitle: function () {
            return MeadCommon.DashboardService.hasWidgetDefinition(this.selected_child_type) ?
                MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type).getAddWidgetTitle() : '';
        },
        /**
         * Description for widget
         */
        uiWidgetDesc: function () {
            return MeadCommon.DashboardService.hasWidgetDefinition(this.selected_child_type) ?
                MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type).getAddWidgetDesc() : '';
        },
        /**
         * Returns true if a widget is selected
         */
        widgetSelected: function () {
            return this.selected_child_type !== 'none';
        },
        /**
         * Build list of child types for display
         */
        childOptions: function () {
            const list = [];
            const categories = {};
            if (this.rowType.length > 0) {
                for (const child of MeadCommon.DashboardService.getRowDefinition(this.rowType).getAccessibleChildren()) {
                    if (!MeadCommon.DashboardService.hasWidgetDefinition(child)) continue;

                    const category = MeadCommon.DashboardService.getWidgetDefinition(child).getCategory();
                    if (categories[category] === undefined) {
                        categories[category] = {
                            category,
                            items: []
                        }
                    }
                    categories[category].items.push({
                        value: MeadCommon.DashboardService.getWidgetDefinition(child).type,
                        label: MeadCommon.DashboardService.getWidgetDefinition(child).getAddWidgetTitle(),
                        subtitle: MeadCommon.DashboardService.getWidgetDefinition(child).getAddWidgetDesc(),
                        disabled: MeadCommon.DashboardService.getWidgetDefinition(child).getWeight() > this.availableWeight,
                    });
                }
            }
            for (const key in categories) {
                list.push(categories[key]);
            }
            return list;
        },
        /**
         * Returns true if the selected widget has configuration to show in this modal.
         */
        hasConfiguration: function () {
            const widget = MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type);
            return widget && widget.hasEditConfiguration;
        },
        /**
         * Returns the configuration sections for the selected child widget
         */
        configurationSections: function () {
            return MeadCommon.DashboardService.hasWidgetDefinition(this.selected_child_type) ?
                MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type).getConfigurationSections() : [];
        },
        /**
         * Check if the modal should be presented full screen
         */
        isFullScreen: function () {
            if (!this.widgetSelected) return false;

            if (this.step < 2) return false;

            return MeadCommon.DashboardService.hasWidgetDefinition(this.selected_child_type) ?
                MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type).isFullScreenInEditMode() : false;
        },
        /**
         * If we should show the continue button instead of the
         * "Add to Dashboard button"
         */
        showContinueButton: function () {
            return this.step === 1 &&
                (!this.widgetSelected || (this.widgetSelected && this.hasConfiguration));
        },
        /**
         * If the selected row type only has one valid child type
         */
        hasOneChildType: function () {
            return this.rowType.length === 0 ||
                MeadCommon.DashboardService.getRowDefinition(this.rowType)?.getAccessibleChildren().length === 1;
        },
        /**
         * Returns the results of configuration validation
         */
        configValidation: function () {
            if (!this.hasWidgetDefinition) return new MeadCommon.DashboardValidationResults();
            return this.widgetDefinition.isConfigValid(this.child_props);

        },
        /**
         * Returns true or false if the config is valid for the selected widget
         */
        isConfigValid: function () {
            return this.configValidation.isValid;
        },
        /**
         * Returns true if we have a widget definition
         */
        hasWidgetDefinition: function () {
            return MeadCommon.DashboardService.hasWidgetDefinition(this.selected_child_type);
        },
        /**
         * Returns current widget definition
         */
        widgetDefinition: function () {
            if (!this.hasWidgetDefinition) return new MeadCommon.DashboardWidgetDefinition();
            return MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type);
        }
    },
    methods: {
        /**
         * Show config validation popover
         */
        onShowConfigValidation: function (event) {
            MeadCommon.PopoverService.openPopover(new MeadCommon.PopoverConfig()
                .setEvent(event)
                .setComponent(DashboardConfigValidationPopover)
                .setShowArrow(true)
                .setProps({
                    validation: this.configValidation
                }))
        },
        /**
         * Fetches component for selected widget
         */
        getWidget: function () {
            return MeadCommon.DashboardService.getDashboardWidgetComponent(this.selected_child_type)
        },
        /**
         * When widget selection changes from the dropdown
         */
        onWidgetSelected: function (type) {
            this.selected_child_type = type;
            if (!this.widgetSelected) {
                this.child_props = {};
            } else {
                this.child_props = MeadCommon.DashboardService.hasWidgetDefinition(this.selected_child_type) ?
                    MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type).getDefaultProps() : {}
            }
        },
        /**
         * Configuration updated from child configuration widgets
         * @param options
         */
        onConfigUpdated: function (options) {
            // Some value types were not getting proper updates with watchers
            // in dashboard widgets. Stringify and parse made those updates work.
            this.child_props = JSON.parse(JSON.stringify(options));
        },
        onConfirm: async function () {
            this.$emit('confirm', {
                type: this.selected_child_type,
                options: this.child_props,
            });
            const widget = MeadCommon.DashboardService.getWidgetDefinition(this.selected_child_type);
            const basic_type = MeadCommon.I18N.go(widget ? widget.getBasicTypeLabel() : '');
            const title = this.edit_mode ? 'mead.x_updated' : 'mead.x_added';
            const desc = this.edit_mode ? 'mead.the_x_was_updated_successfully' : 'mead.the_x_was_added_successfully';
            MeadCommon.NotificationService.tsuccess(MeadCommon.I18N.go(desc, {type: basic_type}),
                {
                    title: MeadCommon.I18N.go(title, {value: basic_type})
                });
            this.onClose();
        },
        onClose: function () {
            this.$emit('close');
        },
        onCancel: function () {
            this.$emit('close');
        },
        /**
         * If the selected row type only has one valid child type, the
         * auto advance to step 2.
         */
        checkForSingleChildMode: function () {
            // Skip in edit mode
            if (this.edit_mode) return;

            // If row has one child widget type, auto select that
            if (this.hasOneChildType) {
                const onlyChildType = MeadCommon.DashboardService.getRowDefinition(this.rowType).getAccessibleChildren()[0];
                if (MeadCommon.DashboardService.getWidgetDefinition(onlyChildType).getWeight() > this.availableWeight) return;

                this.onWidgetSelected(onlyChildType);
                this.step = 2;
            }
        }
    },
    created: function () {
        this.checkForSingleChildMode();
    }
};
</script>
<style lang="scss" scoped>
@use '../../../../../sass/variables' as *;
.add-edit-modal {
    .divider-line {
        margin-right: -1.5rem;
        margin-left: -1.5rem;
    }

    .widget-blueprint-area {
        background-color: var(--c-page-bg);
        box-shadow: inset 0 0 10px var(--c-panel-border);

        .widget-item {
            box-shadow: var(--c-shade-l);
        }
    }

    &.el--fullscreen {
        height: 100vh;
        width: 100vw;
        overflow-y: scroll;
        border-radius: 0;
    }

    // Mobile view
    @media screen and (max-width: $mq-ds-break) {
        .divider-line {
            display: none;
        }

        .configuration-section {
            border-right: none;
            padding-right: 0.5rem;
        }

        .widget-preview-section {
            padding-left: 0.5rem;
        }
    }

    &:not(.el--fullscreen) {
        // Desktop view
        @media screen and (min-width: $mq-ds-break) {
            width: 45rem;

            &.widget-selected {
                width: 100rem;
            }
        }
    }
}

</style>
